import {
  EstadoDeLaEmpresa,
  UsuariosDeLaEmpresa,
  Vigencia,
} from '../components/estado-de-la-empresa';
import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, Search, Eye, CreditCard, Copy, Plus, Trash2 } from 'lucide-react';
import { useAdminStore } from '../store/admin-store';
import { SelectorDePlan } from '../components/selector-de-plan';
import { Pagination } from '@/components/ui/pagination';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { MobileCardList } from '@/components/ui/mobile-card-list';
import { ClonarEmpresaDialog } from '../components/clonar-empresa-dialog';
import { NuevaEmpresaDialog } from '../components/nueva-empresa-dialog';
import { EliminarEmpresaDialog } from '../components/eliminar-empresa-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { RUBROS, type FiltroDeDemo, type TipoDeEmpresa } from '../types/admin.types';
import { PastillasDeLaDemo } from '../components/pastillas-de-la-demo';
import { OPCIONES_DE_FILTRO_DE_DEMO } from '../lib/demo-textos';

const TIER_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  TRIAL: 'secondary',
  BASIC: 'outline',
  PROFESSIONAL: 'default',
  ENTERPRISE: 'destructive',
};

/** El plan contratado, o que no hay ninguno. Se ve igual en tabla y tarjeta. */
function PlanDeLaEmpresa({
  subscription,
}: {
  subscription?: { planTier: string; planName: string; isTrial?: boolean } | null;
}) {
  if (!subscription) return <Badge variant="destructive">Sin plan</Badge>;
  return (
    <Badge variant={TIER_VARIANTS[subscription.planTier] || 'outline'}>
      {subscription.planName}
      {subscription.isTrial && ' (Trial)'}
    </Badge>
  );
}

export function AdminTenantsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    tenants,
    tenantsPagination,
    tenantsLoading,
    fetchTenants,
    plans,
    fetchPlans,
    createSubscription,
  } = useAdminStore();
  const [nuevaEmpresa, setNuevaEmpresa] = useState(false);
  const [clonarDialog, setClonarDialog] = useState<{ id: string; nombre: string } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Reglas de la demo (343): «lista para la venta», «sin ingresos» o «rubro sin tiendas».
  // Demo que vence (C): el aviso de la campanita llega con `?demo=por-vencer|vencida`.
  const [parametros] = useSearchParams();
  const [demoFilter, setDemoFilter] = useState<'todas' | FiltroDeDemo>(() => {
    const desdeLaUrl = parametros.get('demo');
    return OPCIONES_DE_FILTRO_DE_DEMO.some((o) => o.valor === desdeLaUrl)
      ? (desdeLaUrl as FiltroDeDemo)
      : 'todas';
  });
  const [page, setPage] = useState(1);
  const [assignDialog, setAssignDialog] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [endDate, setEndDate] = useState('');
  const [empresaAEliminar, setEmpresaAEliminar] = useState<(typeof tenants)[number] | null>(null);

  useEffect(() => {
    fetchTenants({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      demo: demoFilter === 'todas' ? undefined : demoFilter,
      page,
    });
  }, [search, statusFilter, demoFilter, page, fetchTenants]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleAssignPlan = async () => {
    if (!assignDialog || !selectedPlan || !endDate) return;
    try {
      await createSubscription({
        tenantId: assignDialog,
        planId: selectedPlan,
        endDate: new Date(endDate).toISOString(),
      });
      setAssignDialog(null);
      setSelectedPlan('');
      setEndDate('');
    } catch (err) {
      console.error('Error asignando plan:', err);
    }
  };

  /* Las acciones de una empresa, en un solo lugar: las usan la tabla y la tarjeta. */
  const accionesDe = (tenant: (typeof tenants)[number]) => [
    {
      label: 'Ver detalle',
      icon: <Eye className="h-4 w-4" />,
      onSelect: () => navigate(`/plataforma/empresas/${tenant.id}`),
    },
    {
      label: tenant.subscription ? 'Cambiar plan' : 'Asignar plan',
      icon: <CreditCard className="h-4 w-4" />,
      onSelect: () => setAssignDialog(tenant.id),
    },
    {
      label: 'Eliminar empresa',
      icon: <Trash2 className="h-4 w-4" />,
      destructive: true,
      separadorAntes: true,
      onSelect: () => setEmpresaAEliminar(tenant),
    },
    {
      label: 'Copiar a una empresa nueva',
      icon: <Copy className="h-4 w-4" />,
      onSelect: () => setClonarDialog({ id: tenant.id, nombre: tenant.name }),
    },
  ];

  /* Las mismas empresas, apiladas, para un teléfono. */
  /*
    El estado de una empresa en una sola palabra, para la franja de la tarjeta.
    El orden importa: una empresa inactiva es un problema aunque su plan esté al
    día, así que eso se mira primero.
  */
  const estadoDe = (tenant: (typeof tenants)[number]) => {
    // Archivada: fuera de uso a propósito, no es un problema que mirar.
    if (tenant.archivedAt) return 'neutro' as const;
    if (!tenant.isActive) return 'mal' as const;
    if (!tenant.subscription) return 'neutro' as const;
    const dias = Math.ceil(
      (new Date(tenant.subscription.endDate).getTime() - Date.now()) / 86_400_000
    );
    if (dias < 0) return 'mal' as const;
    if (dias <= 7) return 'atencion' as const;
    return 'bien' as const;
  };

  const tarjetas = tenants.map((tenant) => ({
    id: tenant.id,
    estado: estadoDe(tenant),
    onOpen: () => navigate(`/plataforma/empresas/${tenant.id}`),
    titulo: tenant.name,
    subtitulo: tenant.email || tenant.slug,
    insignia: <EstadoDeLaEmpresa activa={tenant.isActive} archivadaEl={tenant.archivedAt} />,
    acciones: <RowActionsMenu descripcion={tenant.name} acciones={accionesDe(tenant)} />,
    campos: [
      {
        etiqueta: 'Rubro',
        valor: (
          <Badge variant="outline" className={RUBROS[tenant.type as TipoDeEmpresa]?.clases}>
            {RUBROS[tenant.type as TipoDeEmpresa]?.etiqueta ?? tenant.type}
          </Badge>
        ),
      },
      { etiqueta: 'Plan', valor: <PlanDeLaEmpresa subscription={tenant.subscription} /> },
      {
        etiqueta: 'Usuarios',
        valor: <UsuariosDeLaEmpresa cantidad={tenant.userCount} archivada={!!tenant.archivedAt} />,
      },
      {
        etiqueta: 'Vence',
        valor: (
          <Vigencia
            endDate={tenant.demo?.vencimiento?.venceEl ?? tenant.subscription?.endDate}
            archivada={!!tenant.archivedAt}
            esDemo={tenant.esDemo}
          />
        ),
        ancho: true,
      },
      // Una empresa en prueba cuenta cómo va; una con plan completo no tiene este renglón.
      ...(tenant.demo
        ? [
            {
              etiqueta: 'Prueba',
              valor: <PastillasDeLaDemo demo={tenant.demo} misiones={tenant.misiones} />,
              ancho: true,
            },
          ]
        : []),
    ],
  }));

  return (
    <AppLayout variante="plataforma">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6" />
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Empresas</h1>
            {tenantsPagination && (
              <span className="text-sm text-muted-foreground tabular-nums">
                {tenantsPagination.total}
              </span>
            )}
          </div>
          {/*
            El alta no existía: una empresa solo podía nacer por el formulario
            público de registro. Para incorporar a un cliente había que
            registrarlo como si fuera un desconocido que llegó a la web.
          */}
          <Button onClick={() => setNuevaEmpresa(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Nueva empresa
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar empresa..."
              className="pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="archived">Archivadas</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={demoFilter}
            onValueChange={(v) => {
              setDemoFilter(v as 'todas' | FiltroDeDemo);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[220px]" aria-label="Filtrar demos">
              <SelectValue placeholder="Demos" />
            </SelectTrigger>
            <SelectContent>
              {OPCIONES_DE_FILTRO_DE_DEMO.map((o) => (
                <SelectItem key={o.valor} value={o.valor}>
                  {o.texto}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {tenantsLoading ? (
              <div className="p-6 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : (
              <>
                {/* Seis columnas tampoco entran en un teléfono. */}
                <div className="md:hidden p-3">
                  <MobileCardList items={tarjetas} vacio="No hay empresas" />
                </div>

                <Table className="hidden md:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Rubro</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Usuarios</TableHead>
                      <TableHead>Vence</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenants.map((tenant) => (
                      <TableRow
                        key={tenant.id}
                        role="link"
                        tabIndex={0}
                        aria-label={`Abrir ${tenant.name}`}
                        className="cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                        onClick={() => navigate(`/plataforma/empresas/${tenant.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/plataforma/empresas/${tenant.id}`);
                          }
                        }}
                      >
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2 font-medium">
                              {tenant.name}
                              {tenant.esDemo && <Badge variant="secondary">Demo</Badge>}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {tenant.email || tenant.slug}
                            </div>
                            <PastillasDeLaDemo demo={tenant.demo} misiones={tenant.misiones} />
                          </div>
                        </TableCell>
                        {/*
                          El rubro va pegado al nombre y antes del plan: es lo que
                          responde "¿esta empresa con qué se compara?", que se pregunta
                          antes que cuánto paga.
                        */}
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={RUBROS[tenant.type as TipoDeEmpresa]?.clases}
                          >
                            {RUBROS[tenant.type as TipoDeEmpresa]?.etiqueta ?? tenant.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <PlanDeLaEmpresa subscription={tenant.subscription} />
                        </TableCell>
                        <TableCell>
                          <EstadoDeLaEmpresa
                            activa={tenant.isActive}
                            archivadaEl={tenant.archivedAt}
                          />
                        </TableCell>
                        <TableCell className="tabular-nums">
                          <UsuariosDeLaEmpresa
                            cantidad={tenant.userCount}
                            archivada={!!tenant.archivedAt}
                          />
                        </TableCell>
                        <TableCell>
                          {/*
                          Antes era solo la fecha pintada de rojo o ámbar. El color por
                          sí solo no dice nada a quien no lo distingue, y una fecha
                          suelta obliga a hacer la cuenta mentalmente: la que importa
                          es "¿le queda poco?", no el 20 de agosto.
                        */}
                          <Vigencia
                            endDate={
                              tenant.demo?.vencimiento?.venceEl ?? tenant.subscription?.endDate
                            }
                            archivada={!!tenant.archivedAt}
                            esDemo={tenant.esDemo}
                          />
                        </TableCell>
                        {/* El menú no navega: si no, abrirlo abriría la fila. */}
                        <TableCell
                          className="text-right"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {/*
                          Eran dos botones de ícono sueltos, que no escalan: al sumar
                          una tercera acción la columna se vuelve una hilera de íconos
                          indistinguibles. El menú además nombra la fila.
                        */}
                          <RowActionsMenu descripcion={tenant.name} acciones={accionesDe(tenant)} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {tenants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No se encontraron empresas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>

        {/*
          Antes este bloque estaba escrito a mano acá, en usuarios y en la bandeja
          de bugs, con el mismo código. Y decía "Página 1 de 5", que no responde la
          pregunta que uno se hace al abrir un listado: cuántas empresas hay.
        */}
        {tenantsPagination && (
          <Pagination
            page={page}
            totalPages={tenantsPagination.totalPages}
            totalItems={tenantsPagination.total}
            pageSize={tenantsPagination.limit}
            onPageChange={setPage}
            disabled={tenantsLoading}
            itemName="empresas"
          />
        )}

        {/* Assign Plan Dialog */}
        <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Asignar Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Plan</label>
                <SelectorDePlan planes={plans} valor={selectedPlan} onCambio={setSelectedPlan} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Fecha de vencimiento</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleAssignPlan}
                disabled={!selectedPlan || !endDate}
              >
                Asignar Plan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <NuevaEmpresaDialog
          abierto={nuevaEmpresa}
          onClose={() => setNuevaEmpresa(false)}
          onCreada={(id) => navigate(`/plataforma/empresas/${id}`)}
        />
        <EliminarEmpresaDialog
          empresa={
            empresaAEliminar
              ? {
                  id: empresaAEliminar.id,
                  name: empresaAEliminar.name,
                  slug: empresaAEliminar.slug,
                }
              : null
          }
          onClose={() => setEmpresaAEliminar(null)}
          onEliminada={() => {
            const nombre = empresaAEliminar?.name;
            setEmpresaAEliminar(null);
            setPage(1);
            fetchTenants({
              search: search || undefined,
              status: statusFilter === 'all' ? undefined : statusFilter,
              demo: demoFilter === 'todas' ? undefined : demoFilter,
              page: 1,
            });
            toast({ title: `La empresa ${nombre} fue eliminada.` });
          }}
        />

        {clonarDialog && (
          <ClonarEmpresaDialog
            empresaId={clonarDialog.id}
            nombreOrigen={clonarDialog.nombre}
            abierto
            onCerrar={() => setClonarDialog(null)}
            onClonada={() =>
              fetchTenants({
                search: search || undefined,
                status: statusFilter === 'all' ? undefined : statusFilter,
                demo: demoFilter === 'todas' ? undefined : demoFilter,
                page,
              })
            }
          />
        )}
      </div>
    </AppLayout>
  );
}
